import React, { useCallback, useEffect, useState } from "react";
import Tree from "react-d3-tree";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// Custom hook for centering the tree
const useCenteredTree = (defaultTranslate = { x: 0, y: 0 }) => {
  const [translate, setTranslate] = useState(defaultTranslate);
  const containerRef = useCallback((containerElem) => {
    if (containerElem !== null) {
      const { width, height } = containerElem.getBoundingClientRect();
      setTranslate({ x: width / 2, y: height / 6 });
    }
  }, []);
  return [translate, containerRef];
};

// Function to transform API response into react-d3-tree format
const transformHierarchyData = (apiData) => {
  if (!apiData || !apiData.body) return {};

  const { parent, user, child } = apiData.body;
  if (!user || user.length === 0) return {}; // Ensure logged-in user exists

  const loggedInUser = user[0];

  // Parent levels ordered from highest to lowest
  const parentLevels = ["CHANNELPARTNER", "SUPERDISTRIBUTOR", "MASTERDISTRIBUTOR", "AREADISTRIBUTOR", "RETAILER"];

  // Group parents by levels
  const groupedParents = {};
  parentLevels.forEach((level) => (groupedParents[level] = []));
  parent.forEach((p) => {
    if (groupedParents[p.userRole]) {
      groupedParents[p.userRole].push({
        name: p.userName,
        attributes: { userId: p.userId, userRole: p.userRole },
        children: [],
        collapsed: true, // Default collapse children
      });
    }
  });

  // Build parent hierarchy from top to bottom
  let parentTree = null;
  parentLevels.forEach((level) => {
    if (groupedParents[level].length > 0) {
      const newParentNode = {
        name: level.toUpperCase(), // Labeling the parent role
        children: groupedParents[level],
      };
      if (!parentTree) {
        parentTree = newParentNode;
      } else {
        newParentNode.children = [parentTree]; // Ensure hierarchical structure
        parentTree = newParentNode;
      }
    }
  });

  // Attach the logged-in user below the lowest parent
  const loggedInUserNode = {
    name: loggedInUser.userName,
    attributes: { userId: loggedInUser.userId, userRole: loggedInUser.userRole },
    children: [],
    collapsed: true, // Initially collapse children
  };

  if (parentTree) {
    // Attach the logged-in user as a child of the lowest parent
    let lastParentNode = parentTree;
    while (lastParentNode.children.length > 0) {
      lastParentNode = lastParentNode.children[0]; // Navigate down to the last child
    }
    lastParentNode.children.push(loggedInUserNode);
  } else {
    // If no parents, the logged-in user becomes the root
    parentTree = loggedInUserNode;
  }

  // Attach children below the logged-in user
  const childNodes = child.length
    ? child.map((c) => ({
        name: c.userName,
        attributes: { userId: c.userId, userRole: c.userRole },
        children: [],
        collapsed: true, // Initially hidden
      }))
    : [{ name: "No Child", collapsed: true }];

  loggedInUserNode.children = childNodes;

  // Construct final tree without "Organization Hierarchy" and parent role node
  return parentTree || {};
};

// Custom node rendering
const renderRectSvgNode = ({ nodeDatum, toggleNode, navigate }) => (
  <g>
    <circle
      r="10"
      onClick={() => {
        if (nodeDatum.children && nodeDatum.children.length > 0) {
          toggleNode(); // Expand/collapse node
        } else {
          navigate(`/user/${nodeDatum.attributes?.userId}`); // Redirect on click
        }
      }}
      style={{ cursor: "pointer" }}
    />
    <text fill="black" strokeWidth="1" x="20">
      {nodeDatum.name}
    </text>
    {nodeDatum.attributes?.userId && (
      <text fill="black" x="20" dy="20" strokeWidth="1">
        ID: {nodeDatum.attributes.userId}
      </text>
    )}
    {nodeDatum.attributes?.userRole && (
      <text fill="black" x="20" dy="40" strokeWidth="1">
        Role: {nodeDatum.attributes.userRole}
      </text>
    )}
  </g>
);

const HeirarchyUsers = () => {
  const [translate, containerRef] = useCenteredTree();
  const [orgChart, setOrgChart] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHierarchyData = async () => {
      const token = sessionStorage.getItem("jwtToken");
      if (!token) {
        console.error("No JWT token found in session storage.");
        return;
      }

      try {
        const response = await axios.get(
          "https://api.ipaisa.co.in/myapp/auth/hierarchyFlowChart",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setOrgChart(transformHierarchyData(response.data));
      } catch (error) {
        console.error("Error fetching hierarchy data:", error);
      }
    };

    fetchHierarchyData();
  }, []);

  if (!orgChart || Object.keys(orgChart).length === 0) {
    return <p>Loading...</p>;
  }

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "white",
      }}
      ref={containerRef}
    >
      <Tree
        data={orgChart}
        translate={translate}
        renderCustomNodeElement={(rd3tProps) =>
          renderRectSvgNode({ ...rd3tProps, navigate })
        }
        orientation="vertical"
        nodeSize={{ x: 300, y: 200 }} // Wider spacing
        separation={{ siblings: 1.5, nonSiblings: 2 }} // Adjust sibling/non-sibling gaps
        collapsible={true} // Allow collapse/expand
      />
    </div>
  );
};

export default HeirarchyUsers;
